export const NOT_FOUND_PATH = '/404';

/**
 * Canonical route paths for the invoice detail page (`InvoicePage`).
 *
 * Single source of truth shared by the router (`MainRoutes.tsx`) and the concurrent-editing
 * ribbon (`useEditableResourceFromRoute.ts`). Every URL that renders `InvoicePage` must live
 * here so the ribbon's presence tracking stays in sync whenever an invoice URL is added or
 * changed: a pattern the router renders but the ribbon does not list silently disables the
 * ribbon on that URL. All variants use `:id` for the invoice id.
 */
export const INVOICE_DETAIL_ROUTE_PATHS = {
    STANDALONE: '/invoice/:id',
    STANDALONE_TAB: '/invoice/:id/:tab',
    CONTACT: '/contact/:contactId/invoice/:id',
    CONTACT_TAB: '/contact/:contactId/invoice/:id/:tab',
    MATTER: '/matter/:matterId/invoice/:id',
    MATTER_TAB: '/matter/:matterId/invoice/:id/:tab',
} as const;

export const INVOICE_DETAIL_ROUTE_PATH_LIST = Object.values(INVOICE_DETAIL_ROUTE_PATHS);

/**
 * The invoice page's own tabs that a create dialog can land back on: `STANDALONE_TAB` renders them, and the page's
 * tab strip resolves the tab from the last segment.
 */
export const INVOICE_TAB_PATHS = {
    TIMECARDS: '/invoice/:id/timecards',
    EXPENSES: '/invoice/:id/expenses',
} as const;

/**
 * Canonical route paths for the matter edit page (`MatterPageEdit`).
 *
 * Shared source of truth for the router (`MainRoutes.tsx`) and the concurrent-editing ribbon
 * (`useEditableResourceFromRoute.ts`), mirroring INVOICE_DETAIL_ROUTE_PATHS. Every URL that
 * renders `MatterPageEdit` must live here so the ribbon stays in sync. The matter edit page
 * does not use path-based tab routing today; if it adopts it, both the route and the ribbon
 * update from one place. All variants use `:id` for the matter id.
 */
export const MATTER_EDIT_ROUTE_PATHS = {
    EDIT: '/matter/:id/edit',
} as const;

export const MATTER_EDIT_ROUTE_PATH_LIST = Object.values(MATTER_EDIT_ROUTE_PATHS);
